import { useState, useEffect, useMemo } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import {
  AlertTriangle, Clock, CheckCircle2, Plus, Trash2, Save, Info, CalendarDays,
} from 'lucide-react';

type Rule = { vatDays: number; ctMonths: number; vatThreshold: number; bookDay: number };
type StandardDate = { name: string; cat: string; date: string };
type Obligation = { n: string; cat: string; due: Date | null; freq: string; std?: boolean };
type StatusKey = 'overdue' | 'due' | 'upcoming' | 'scheduled';

const DEFAULT_RULES: Rule = { vatDays: 28, ctMonths: 9, vatThreshold: 375000, bookDay: 5 };
const DEFAULT_REMINDERS = [90, 30, 21, 14, 7, 6, 5, 4, 3, 2, 1];

// Sample client dates — only used for the live preview (Phase 2 = real per-client data)
const SAMPLE = {
  vatReg: 'Yes', vatFreq: 'Quarterly', vatPeriodEnd: '2026-06-30',
  fyEnd: '2025-12-31', ctReg: 'No', ctDeadline: '2026-09-30',
  licenceExpiry: '2026-08-15', turnover: 420000,
};

const parse = (d: string): Date | null => d ? new Date(d + 'T00:00:00') : null;
const addDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number): Date => {
  const x = new Date(d); const day = x.getDate(); x.setDate(1);
  x.setMonth(x.getMonth() + n);
  x.setDate(Math.min(day, new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate()));
  return x;
};
const getToday = (): Date => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };
const nextMonthDay = (n: number): Date => {
  const t = getToday();
  const day = Math.min(Math.max(1, n), 28); // clamp to avoid month-end overflow
  return new Date(t.getFullYear(), t.getMonth() + 1, day);
};
const fmt = (d: Date | null) =>
  d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysLeft = (d: Date | null): number | null =>
  d ? Math.round((d.getTime() - getToday().getTime()) / 86400000) : null;

const statusOf = (d: Date | null): [string, StatusKey] => {
  const n = daysLeft(d);
  if (n === null) return ['Scheduled', 'scheduled'];
  if (n < 0)      return ['Overdue',   'overdue'];
  if (n <= 30)    return ['Due soon',  'due'];
  if (n <= 90)    return ['Upcoming',  'upcoming'];
  return ['Scheduled', 'scheduled'];
};

const STATUS_STYLES: Record<StatusKey, { dot: string; pill: string }> = {
  overdue:   { dot: 'bg-red-500',   pill: 'bg-red-100 text-red-700' },
  due:       { dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-700' },
  upcoming:  { dot: 'bg-blue-500',  pill: 'bg-blue-100 text-blue-700' },
  scheduled: { dot: 'bg-green-500', pill: 'bg-green-100 text-green-700' },
};

function computeObligations(rules: Rule, standardDates: StandardDate[]): Obligation[] {
  const c = SAMPLE;
  const today = getToday();
  const L: Obligation[] = [];

  if (c.vatReg === 'Yes' && c.vatPeriodEnd) {
    const d = parse(c.vatPeriodEnd);
    L.push({ n: 'VAT return + payment', cat: 'VAT', due: d ? addDays(d, rules.vatDays) : null, freq: c.vatFreq });
  }
  if (c.ctReg === 'Yes' && c.fyEnd) {
    const d = parse(c.fyEnd);
    L.push({ n: 'Corporate Tax return + payment', cat: 'Corporate Tax', due: d ? addMonths(d, rules.ctMonths) : null, freq: 'Annual' });
  }
  if (c.ctReg === 'No' && c.ctDeadline) {
    L.push({ n: 'Corporate Tax registration (pending)', cat: 'Corporate Tax', due: parse(c.ctDeadline), freq: 'One-time' });
  }
  if (c.vatReg === 'No' && c.turnover >= rules.vatThreshold) {
    L.push({ n: 'VAT registration (threshold passed)', cat: 'VAT', due: addDays(today, 20), freq: 'Action' });
  }
  if (c.licenceExpiry) {
    L.push({ n: 'Trade licence renewal', cat: 'Licence', due: parse(c.licenceExpiry), freq: 'Annual' });
  }
  L.push({ n: 'Monthly bookkeeping close', cat: 'Bookkeeping', due: nextMonthDay(rules.bookDay), freq: 'Monthly' });
  L.push({ n: 'Document upload',           cat: 'Bookkeeping', due: nextMonthDay(rules.bookDay), freq: 'Monthly' });

  standardDates.forEach(o =>
    L.push({ n: o.name, cat: o.cat || 'Standard', due: parse(o.date), freq: 'Standard', std: true })
  );
  L.sort((a, b) => (a.due?.getTime() ?? 8e15) - (b.due?.getTime() ?? 8e15));
  return L;
}

export default function AdminComplianceCalendar() {
  const [rules, setRules]               = useState<Rule>(DEFAULT_RULES);
  const [reminders, setReminders]       = useState<number[]>(DEFAULT_REMINDERS);
  const [standardDates, setStandardDates] = useState<StandardDate[]>([]);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState<string | null>(null);
  const [saving, setSaving]             = useState(false);
  const [savedAt, setSavedAt]           = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newCat, setNewCat]   = useState('');
  const [newDate, setNewDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'calendar', 'standard'));
        if (snap.exists()) {
          const data = snap.data();
          if (data.rules)                        setRules({ ...DEFAULT_RULES, ...data.rules });
          if (Array.isArray(data.reminderDays))  setReminders(data.reminderDays);
          if (Array.isArray(data.standardDates)) setStandardDates(data.standardDates);
        }
      } catch (e: any) {
        console.error('Failed to load calendar/standard:', e);
        if (e?.code === 'permission-denied') {
          setLoadError('Firestore permission denied — the admin session is not authenticated with Firebase. Saved settings cannot be loaded or saved. Check Firestore security rules.');
        } else {
          setLoadError(`Failed to load saved settings (${e?.message ?? 'unknown error'}) — showing defaults.`);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const obligations = useMemo(() => computeObligations(rules, standardDates), [rules, standardDates]);

  const overdueCount  = obligations.filter(o => { const n = daysLeft(o.due); return n !== null && n < 0; }).length;
  const dueSoonCount  = obligations.filter(o => { const n = daysLeft(o.due); return n !== null && n >= 0 && n <= 30; }).length;
  const upcomingCount = obligations.filter(o => { const n = daysLeft(o.due); return n !== null && n > 30 && n <= 90; }).length;

  const nextDue      = obligations.find(o => o.due !== null) ?? null;
  const nextDaysLeft = nextDue ? daysLeft(nextDue.due) : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('calendar/standard', { rules, reminderDays: reminders, standardDates });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'calendar/standard');
    } catch (e) {
      console.error('Save failed:', e);
      showAlert('Save failed — check console.');
    } finally {
      setSaving(false);
    }
  };

  const addDate = () => {
    if (!newName.trim() || !newDate) return;
    setStandardDates(prev => [...prev, { name: newName.trim(), cat: newCat.trim() || 'Standard', date: newDate }]);
    setNewName(''); setNewCat(''); setNewDate('');
  };

  const removeDate     = (i: number) => setStandardDates(prev => prev.filter((_, idx) => idx !== i));
  const updateReminder = (i: number, v: number) => setReminders(prev => prev.map((r, idx) => idx === i ? v : r));
  const removeReminder = (i: number) => setReminders(prev => prev.filter((_, idx) => idx !== i));
  const addReminder    = () => setReminders(prev => [...prev, 3]);
  const updateRule     = (key: keyof Rule, v: number) => setRules(prev => ({ ...prev, [key]: v }));

  const heroColor =
    nextDaysLeft !== null && nextDaysLeft < 0  ? 'bg-red-600' :
    nextDaysLeft !== null && nextDaysLeft <= 30 ? 'bg-amber-500' :
    'bg-blue-600';

  const LAYOUT = {
    section: 'Services',
    title: 'Compliance Calendar',
    icon: CalendarDays,
    subtitle: 'Standard UAE schedule — edit once, all client dashboards update.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading compliance calendar…" />;

  return (
    <AdminLayout {...LAYOUT}>
      {/* Firestore error banner */}
      {loadError && (
        <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 mb-4 text-xs font-bold text-red-800">
          <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" />
          <span>{loadError}</span>
        </div>
      )}
      {/* Hero: next deadline */}
      {nextDue && (
        <div className={`rounded-2xl p-5 mb-5 flex items-center gap-6 flex-wrap ${heroColor} text-white shadow-lg`}>
          <CalendarDays className="w-10 h-10 opacity-75 flex-none" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold opacity-70 uppercase tracking-wide mb-1">Next Deadline</div>
            <div className="text-lg font-extrabold leading-tight">{nextDue.n}</div>
            <div className="text-sm opacity-80 font-medium mt-0.5">
              {nextDue.cat} · {nextDue.freq} · {fmt(nextDue.due)}
            </div>
          </div>
          <div className="text-right flex-none">
            <div className="text-5xl font-black tabular-nums leading-none">
              {nextDaysLeft !== null ? Math.abs(nextDaysLeft) : '—'}
            </div>
            <div className="text-sm font-bold opacity-80 mt-1">
              {nextDaysLeft !== null && nextDaysLeft < 0 ? 'days overdue' : 'days left'}
            </div>
          </div>
        </div>
      )}

      {/* Counts */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
          <AlertTriangle className="w-8 h-8 text-red-500 flex-none" />
          <div>
            <div className="text-2xl font-black text-red-600">{overdueCount}</div>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Overdue</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
          <Clock className="w-8 h-8 text-amber-500 flex-none" />
          <div>
            <div className="text-2xl font-black text-amber-600">{dueSoonCount}</div>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Due soon</div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm">
          <CheckCircle2 className="w-8 h-8 text-blue-500 flex-none" />
          <div>
            <div className="text-2xl font-black text-blue-600">{upcomingCount}</div>
            <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Upcoming</div>
          </div>
        </div>
      </div>

      {/* Two-column editor + preview */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-5 items-start">

        {/* LEFT: editor */}
        <div className="space-y-4">

          {/* Statutory rules */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
            <div className="p-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                Statutory rules (auto-calculate every client's deadlines)
              </div>
              <div className="space-y-4">
                {[
                  { key: 'vatDays'      as keyof Rule, label: 'VAT return + payment',                    sub: 'Days after each VAT period end',    unit: 'days after period',  min: 1,  max: 365 },
                  { key: 'ctMonths'     as keyof Rule, label: 'Corporate Tax return + payment',           sub: 'Months after financial-year end',    unit: 'months after FY end', min: 1,  max: 24  },
                  { key: 'vatThreshold' as keyof Rule, label: 'VAT registration threshold',               sub: 'Mandatory registration turnover',    unit: 'AED turnover',        min: 0,  max: 10000000 },
                  { key: 'bookDay'      as keyof Rule, label: 'Monthly bookkeeping / document upload',    sub: 'Day of the following month (1–28)',   unit: 'of next month',       min: 1,  max: 28  },
                ].map(({ key, label, sub, unit, min, max }) => (
                  <div key={key} className="grid grid-cols-[1fr_100px_auto] gap-3 items-center">
                    <div>
                      <div className="font-bold text-sm text-gray-900">{label}</div>
                      <div className="text-xs text-gray-400 font-medium">{sub}</div>
                    </div>
                    <input
                      type="number" value={rules[key]} min={min} max={max}
                      onChange={e => updateRule(key, Math.min(max, Math.max(min, +e.target.value || min)))}
                      className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-bold bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400 w-full"
                    />
                    <span className="text-xs text-gray-400 font-bold whitespace-nowrap">{unit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Reminder cadence */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
            <div className="p-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                Reminder cadence — days before each deadline
              </div>
              <div className="flex flex-wrap gap-2">
                {reminders.map((v, i) => (
                  <div key={i} className="flex items-center gap-1 border-2 border-gray-200 rounded-full px-3 py-1.5">
                    <input
                      type="number" value={v}
                      onChange={e => updateReminder(i, +e.target.value || 0)}
                      className="w-10 bg-transparent border-none outline-none font-bold text-sm text-right text-gray-900"
                    />
                    <span className="text-gray-400 text-xs font-bold">d</span>
                    <button
                      onClick={() => removeReminder(i)}
                      className="text-red-400 hover:text-red-600 ml-1 font-bold leading-none"
                    >×</button>
                  </div>
                ))}
                <button
                  onClick={addReminder}
                  className="flex items-center gap-1 border-2 border-dashed border-gray-300 rounded-full px-3 py-1.5 text-sm font-bold text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> add
                </button>
              </div>
            </div>
          </div>

          {/* Standard dates */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
            <div className="p-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                Standard dates everyone sees (add / remove)
              </div>
              <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Name</label>
                  <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                    placeholder="e.g. ESR notification deadline"
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Category</label>
                  <input type="text" value={newCat} onChange={e => setNewCat(e.target.value)}
                    placeholder="e.g. ESR"
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Date</label>
                  <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:border-blue-400"
                  />
                </div>
                <button
                  onClick={addDate}
                  className="bg-gray-900 text-white rounded-xl px-4 py-2 text-sm font-bold hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>

              {standardDates.length > 0 && (
                <div className="mt-4 space-y-2">
                  {standardDates.map((o, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 border border-gray-200 rounded-xl px-4 py-2.5">
                      <div>
                        <span className="font-bold text-sm text-gray-900">{o.name}</span>
                        <span className="text-gray-400 text-xs font-semibold ml-2">
                          · {o.cat} · {fmt(parse(o.date))}
                        </span>
                      </div>
                      <button onClick={() => removeDate(i)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: preview + save */}
        <div className="xl:sticky xl:top-[88px]">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
            <div className="p-5">
              <div className="flex gap-2 items-start bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 text-xs font-bold text-blue-800">
                <Info className="w-4 h-4 flex-none stroke-blue-500 mt-0.5" />
                <span>Client dashboard preview — the standard schedule clients will see (sample client dates used for the statutory items).</span>
              </div>

              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                Upcoming obligations
              </div>

              <div className="divide-y divide-gray-100">
                {obligations.map((o, i) => {
                  const [statusLabel, statusKey] = statusOf(o.due);
                  const s = STATUS_STYLES[statusKey];
                  return (
                    <div key={i} className="flex items-center gap-3 py-3">
                      <span className={`w-2.5 h-2.5 rounded-full flex-none ${s.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm text-gray-900 truncate">
                          {o.n}
                          {o.std && <span className="text-blue-500 font-semibold ml-1.5 text-xs">·standard</span>}
                        </div>
                        <div className="text-xs text-gray-400 font-medium">{o.cat} · {o.freq}</div>
                      </div>
                      <div className="text-right flex-none">
                        <div className="font-extrabold text-sm text-gray-900">{fmt(o.due)}</div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full inline-block mt-0.5 ${s.pill}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save */}
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-gray-900 font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save & push to all clients'}
                </button>
                {savedAt && (
                  <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs">
                    <CheckCircle2 className="w-4 h-4" />
                    Saved · pushed at {savedAt}
                  </span>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
